import { useMemo, useState, type FormEvent } from 'react';
import { Mail, Inbox as InboxIcon, FileDown, ExternalLink } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Select } from '@/components/ui/Field';
import { Skeleton } from '@/components/ui/Skeleton';
import { Textarea } from '@/components/ui/Field';
import { DataTable } from '@/components/tables/DataTable';
import {
  useAdminContactSubmissions,
  useUpdateContactStatus,
  useInboxApplications,
  useUpdateInboxApplicationStatus,
  getApplicationCvUrl,
  type ContactSubmission,
  type ApplicationSubmission,
} from '@/lib/admin-queries';
import { formatRelative, formatDate } from '@/lib/utils';
import { toast } from '@/components/ui/Toast';

export function InboxPage() {
  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">Inbox</p>
        <h1 className="mt-2 display-serif text-display-l text-fg">Submissions</h1>
        <p className="mt-2 text-sm text-fg-muted">Contact form submissions and job applications.</p>
      </header>

      <Tabs defaultValue="contact">
        <TabsList>
          <TabsTrigger value="contact">
            <InboxIcon className="mr-2 h-3.5 w-3.5" /> Contact
          </TabsTrigger>
          <TabsTrigger value="applications">
            <Mail className="mr-2 h-3.5 w-3.5" /> Applications
          </TabsTrigger>
        </TabsList>

        <TabsContent value="contact">
          <ContactInbox />
        </TabsContent>
        <TabsContent value="applications">
          <ApplicationsInbox />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ContactInbox() {
  const { data: rows = [], isLoading } = useAdminContactSubmissions();
  const updateStatus = useUpdateContactStatus();
  const [selected, setSelected] = useState<ContactSubmission | null>(null);
  const [reply, setReply] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return rows;
    return rows.filter((r) => r.status === statusFilter);
  }, [rows, statusFilter]);

  const columns = useMemo(
    () => [
      { key: 'when', header: 'When', cell: (r: ContactSubmission) => <span className="text-xs text-fg-muted">{formatRelative(r.created_at)}</span> },
      { key: 'from', header: 'From', cell: (r: ContactSubmission) => <span className="font-medium">{r.name}</span> },
      { key: 'topic', header: 'Topic', cell: (r: ContactSubmission) => <Badge variant="gold">{r.topic}</Badge> },
      { key: 'email', header: 'Email', cell: (r: ContactSubmission) => <span className="text-fg-muted">{r.email}</span> },
      {
        key: 'status',
        header: 'Status',
        cell: (r: ContactSubmission) => (
          <Badge variant={r.status === 'new' ? 'warning' : r.status === 'spam' ? 'default' : 'success'}>{r.status}</Badge>
        ),
      },
    ],
    [],
  );

  if (isLoading) {
    return <Skeleton className="h-96 w-full" />;
  }

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Card className="lg:col-span-2">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-8 w-36 text-xs">
            <option value="all">All status</option>
            <option value="new">New</option>
            <option value="replied">Replied</option>
            <option value="resolved">Resolved</option>
            <option value="spam">Spam</option>
          </Select>
          <span className="text-xs text-fg-subtle">{filtered.length} total</span>
        </div>
        <DataTable
          rows={filtered}
          columns={columns}
          rowKey={(r) => r.id}
          onRowClick={(r) => setSelected(r)}
          empty="No contact submissions."
        />
      </Card>
      <Card className="lg:col-span-1">
        <ContactDetail submission={selected} reply={reply} setReply={setReply} updateStatus={updateStatus} />
      </Card>
    </div>
  );
}

function ContactDetail({
  submission,
  reply,
  setReply,
  updateStatus,
}: {
  submission: ContactSubmission | null;
  reply: string;
  setReply: (v: string) => void;
  updateStatus: ReturnType<typeof useUpdateContactStatus>;
}) {
  if (!submission) {
    return <div className="p-8 text-center text-sm text-fg-subtle">Select a submission to read.</div>;
  }
  return (
    <div className="space-y-4 p-5">
      <div>
        <p className="text-xs uppercase tracking-wider text-fg-subtle">{submission.topic}</p>
        <h3 className="mt-1 text-base font-semibold">{submission.name}</h3>
        <p className="text-xs text-fg-muted">{submission.email}{submission.company ? ` · ${submission.company}` : ''}</p>
        <p className="mt-1 text-[10px] text-fg-subtle">{formatDate(submission.created_at)}</p>
      </div>
      <div className="rounded-md border border-border bg-bg-subtle/40 p-3 text-sm leading-relaxed text-fg">
        {submission.message}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs text-fg-subtle">Status</span>
        <Select
          value={submission.status}
          onChange={(e) => updateStatus.mutate({ id: submission.id, status: e.target.value as ContactSubmission['status'] })}
          className="h-8 text-xs"
        >
          <option value="new">New</option>
          <option value="replied">Replied</option>
          <option value="resolved">Resolved</option>
          <option value="spam">Spam</option>
        </Select>
      </div>
      <form
        onSubmit={(e: FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          if (!reply.trim()) return;
          // The notify Edge Function is called directly via the browser; in v1 we
          // update the status and let the operator copy the reply to their email
          // client. (Full in-app email send requires server-side creds.)
          window.location.href = `mailto:${submission.email}?subject=${encodeURIComponent(`Re: ${submission.topic}`)}&body=${encodeURIComponent(reply)}`;
          updateStatus.mutate({ id: submission.id, status: 'replied' });
          toast.success('Opened your mail client. Marked as replied.');
          setReply('');
        }}
        className="space-y-2"
      >
        <Textarea
          rows={5}
          placeholder="Compose a reply (opens your mail client)"
          value={reply}
          onChange={(e) => setReply(e.target.value)}
        />
        <Button type="submit" size="sm" className="w-full">
          Send via mail client
        </Button>
      </form>
    </div>
  );
}

function ApplicationsInbox() {
  const { data: rows = [], isLoading } = useInboxApplications();
  const updateStatus = useUpdateInboxApplicationStatus();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selected, setSelected] = useState<ApplicationSubmission | null>(null);

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return rows;
    return rows.filter((r) => r.status === statusFilter);
  }, [rows, statusFilter]);

  const columns = useMemo(
    () => [
      {
        key: 'when',
        header: 'When',
        cell: (r: ApplicationSubmission) => <span className="text-xs text-fg-muted">{formatRelative(r.created_at)}</span>,
      },
      {
        key: 'applicant',
        header: 'Applicant',
        cell: (r: ApplicationSubmission) => (
          <div>
            <p className="font-medium">{r.full_name}</p>
            <p className="text-xs text-fg-subtle">{r.email}</p>
          </div>
        ),
      },
      {
        key: 'role',
        header: 'Role',
        cell: (r: ApplicationSubmission) => <Badge variant="gold">{r.role_slug ?? 'Open'}</Badge>,
      },
      {
        key: 'status',
        header: 'Status',
        cell: (r: ApplicationSubmission) => <Badge>{r.status}</Badge>,
      },
      {
        key: 'cv',
        header: 'CV',
        cell: (r: ApplicationSubmission) =>
          r.cv_path ? (
            <button
              type="button"
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  const url = await getApplicationCvUrl(r.cv_path as string);
                  window.open(url, '_blank', 'noopener');
                } catch (err) {
                  toast.error((err as Error).message);
                }
              }}
              className="inline-flex h-7 items-center gap-1 rounded-md border border-border px-2 text-xs text-fg-muted hover:border-gold-500/40 hover:text-gold-500"
            >
              <FileDown className="h-3 w-3" /> Download
            </button>
          ) : (
            <span className="text-xs text-fg-subtle">—</span>
          ),
      },
    ],
    [],
  );

  if (isLoading) return <Skeleton className="h-96 w-full" />;

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Card className="lg:col-span-2">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-8 w-36 text-xs">
            <option value="all">All status</option>
            <option value="new">New</option>
            <option value="reviewing">Reviewing</option>
            <option value="interview">Interview</option>
            <option value="offer">Offer</option>
            <option value="rejected">Rejected</option>
            <option value="hired">Hired</option>
          </Select>
          <span className="text-xs text-fg-subtle">{filtered.length} total</span>
        </div>
        <DataTable
          rows={filtered}
          columns={columns}
          rowKey={(r) => r.id}
          onRowClick={(r) => setSelected(r)}
          empty="No applications yet."
        />
      </Card>
      <Card className="lg:col-span-1">
        {selected ? (
          <ApplicationDetail application={selected} updateStatus={updateStatus} />
        ) : (
          <div className="p-8 text-center text-sm text-fg-subtle">Select an application to read.</div>
        )}
      </Card>
    </div>
  );
}

function ApplicationDetail({
  application,
  updateStatus,
}: {
  application: ApplicationSubmission;
  updateStatus: ReturnType<typeof useUpdateInboxApplicationStatus>;
}) {
  async function downloadCv() {
    if (!application.cv_path) return;
    try {
      const url = await getApplicationCvUrl(application.cv_path);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      toast.error((err as Error).message);
    }
  }
  return (
    <div className="space-y-4 p-5">
      <div>
        <p className="text-xs uppercase tracking-wider text-fg-subtle">{application.role_slug ?? 'Open application'}</p>
        <h3 className="mt-1 text-base font-semibold">{application.full_name}</h3>
        <p className="text-xs text-fg-muted">
          {application.email}
          {application.phone ? ` · ${application.phone}` : ''}
        </p>
        <p className="mt-1 text-[10px] text-fg-subtle">Applied {formatDate(application.created_at)}</p>
      </div>
      {application.linkedin_url && (
        <a href={application.linkedin_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-gold-500 hover:underline">
          LinkedIn <ExternalLink className="h-3 w-3" />
        </a>
      )}
      {application.portfolio_url && (
        <a href={application.portfolio_url} target="_blank" rel="noopener noreferrer" className="ml-3 inline-flex items-center gap-1 text-xs text-gold-500 hover:underline">
          Portfolio <ExternalLink className="h-3 w-3" />
        </a>
      )}
      {application.cover_letter && (
        <div className="rounded-md border border-border bg-bg-subtle/40 p-3 text-sm leading-relaxed text-fg">
          {application.cover_letter}
        </div>
      )}
      {application.cv_path && (
        <Button variant="secondary" size="sm" className="w-full" onClick={downloadCv}>
          <FileDown className="h-3.5 w-3.5" /> Download CV
        </Button>
      )}
      <div className="flex items-center gap-2">
        <span className="text-xs text-fg-subtle">Status</span>
        <Select
          value={application.status}
          onChange={(e) => updateStatus.mutate({ id: application.id, status: e.target.value as ApplicationSubmission['status'] })}
          className="h-8 text-xs"
        >
          <option value="new">New</option>
          <option value="reviewing">Reviewing</option>
          <option value="interview">Interview</option>
          <option value="offer">Offer</option>
          <option value="rejected">Rejected</option>
          <option value="hired">Hired</option>
        </Select>
      </div>
      {application.consent_at && (
        <p className="text-[10px] text-fg-subtle">
          Consent given {formatRelative(application.consent_at)}.
        </p>
      )}
    </div>
  );
}